import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { isNull } from 'lodash/fp';

import { RiskAssociated, RiskAssociatedValue, TextRiskField } from '../../../admin/risk-tolerance/store';
import { riskAssociatedLabel } from '../../../admin/risk-tolerance/utils';
import { DeleteButton } from '../../button/DeleteButton';
import { PlusButton } from '../../button/PlusButton';
import { Icon } from '../../icon/Icon';
import { CaretDown, CaretSide, Text as TextIcon } from '../../icons';
import { Text } from '../../text/Text';
import styles from './RiskField.module.scss';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

interface TextRiskFieldConfigProps {
    riskField: TextRiskField;
    riskAssociated: RiskAssociated;
    updateRiskConfig: (riskAssociated: RiskAssociated, value: RiskAssociatedValue) => void;
}

export const TextRiskFieldConfig: React.FC<TextRiskFieldConfigProps> = ({ riskField, riskAssociated, updateRiskConfig }) => {
    const [isOpenSection, setIsOpenSection] = useState<boolean>(false);
    const [indexToDelete, setIndexToDelete] = useState<number | null>(null);

    const { riskConfig } = riskField;

    const sectionOpenIcon = useMemo(() => isOpenSection ? CaretDown : CaretSide, [isOpenSection]);
    const toggleSectionOpen = useCallback(() => setIsOpenSection(!isOpenSection), [isOpenSection]);

    const riskAssociatedExamples = useMemo(() => riskConfig[riskAssociated], [riskConfig, riskAssociated]);

    const sectionTitle = useMemo(() => `${riskAssociatedExamples.length} Example${riskAssociatedExamples.length === 0 || riskAssociatedExamples.length > 1 ? 's' : ''}`, [riskAssociatedExamples.length]);

    const createNewExample = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpenSection(true);
        updateRiskConfig(riskAssociated, [...riskAssociatedExamples, '']);
    }, [riskAssociatedExamples, updateRiskConfig, riskAssociated]);

    const updateExample = useCallback((updatedValue: string, index: number) => {
        const updatedConfig = riskAssociatedExamples.map((example, i) => index === i ? updatedValue : example);
        updateRiskConfig(riskAssociated, updatedConfig);
    }, [riskAssociatedExamples, riskAssociated, updateRiskConfig]);

    const removeExample = useCallback(() => {
        if (!isNull(indexToDelete)) {
            updateRiskConfig(riskAssociated, riskAssociatedExamples.filter((_, i) => i !== indexToDelete));
        }
        setIndexToDelete(null);
    }, [riskAssociatedExamples, riskAssociated, updateRiskConfig, indexToDelete]);

    const showConfirmDelete = useCallback((index: number) => setIndexToDelete(index), []);
    const closeConfirmDelete = useCallback(() => setIndexToDelete(null), []);

    return (
        <div className={styles.riskTextWrapper}>
            <div className={styles.sectionHeader} onClick={toggleSectionOpen}>
                <div className={styles.sectionTitle}>
                    <div className={styles.sectionOpenIcon}>
                        <Icon icon={sectionOpenIcon} fontSize={16} />
                    </div>
                    <div className={styles.sectionLabel}>{sectionTitle}</div>
                </div>
                <div className={styles.addNewExampleWrapper}>
                    <PlusButton onClick={createNewExample} fontSize={20} />
                </div>
            </div>
            {isOpenSection &&
                <div className={styles.riskExamplesWrapper}>
                    {riskAssociatedExamples.map((value, index) => (
                        <div key={index} className={styles.riskExample}>
                            <Text
                                value={value}
                                onChange={e => updateExample(e.currentTarget.value, index)}
                                testId={`risk-example-${riskAssociatedLabel[riskAssociated]}-${index}`}
                            />
                            <div className={styles.iconWrapper}>
                                <DeleteButton onClick={() => showConfirmDelete(index)} />
                            </div>
                        </div>
                    ))}
                </div>
            }
            {!isNull(indexToDelete) &&
                <DeleteConfirmationModal
                    deleteExample={removeExample}
                    closeModal={closeConfirmDelete}
                    icon={TextIcon}
                >
                    <div className={styles.deleteTextExample}>{riskAssociatedExamples[indexToDelete]}</div>
                </DeleteConfirmationModal>
            }
        </div>
    );
};
